import { PX_POR_MM_NOMINAL } from '../catalogos/colposcopia.js';
export class GeometriaInvalida extends Error {
}
export function distancia(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
}
function cruz(a, b, c) {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}
function sobreSegmento(a, b, p) {
    return (cruz(a, b, p) === 0 &&
        Math.min(a.x, b.x) <= p.x &&
        p.x <= Math.max(a.x, b.x) &&
        Math.min(a.y, b.y) <= p.y &&
        p.y <= Math.max(a.y, b.y));
}
/**
 * Por que un poligono no sirve, o `null` si es simple: al menos tres vertices
 * distintos, sin cruzarse ni tocarse a si mismo y con area mayor que cero.
 */
export function problemaDelPoligono(vertices) {
    if (vertices.length < 3)
        return 'El polígono requiere al menos tres vértices.';
    const distintos = new Set(vertices.map((v) => `${v.x},${v.y}`));
    if (distintos.size !== vertices.length)
        return 'El polígono tiene vértices repetidos.';
    const aristas = vertices.map((v, i) => [v, vertices[(i + 1) % vertices.length]]);
    for (let i = 0; i < aristas.length; i += 1) {
        for (let j = 0; j < aristas.length; j += 1) {
            // Las aristas vecinas comparten un vertice: no cuentan como cruce.
            if (j <= i + 1 || (i === 0 && j === aristas.length - 1))
                continue;
            const [a, b] = aristas[i];
            const [c, d] = aristas[j];
            if ((cruz(a, b, c) * cruz(a, b, d) < 0 && cruz(c, d, a) * cruz(c, d, b) < 0) ||
                sobreSegmento(a, b, c) ||
                sobreSegmento(a, b, d) ||
                sobreSegmento(c, d, a) ||
                sobreSegmento(c, d, b)) {
                return 'El polígono no debe cruzarse ni tocarse a sí mismo.';
            }
        }
    }
    if (areaSinValidar(vertices) === 0)
        return 'El polígono debe encerrar un área mayor que cero.';
    return null;
}
function areaSinValidar(vertices) {
    let suma = 0;
    for (let i = 0; i < vertices.length; i += 1) {
        const a = vertices[i];
        const b = vertices[(i + 1) % vertices.length];
        suma += a.x * b.y - b.x * a.y;
    }
    return Math.abs(suma) / 2;
}
/** Area por la formula del cordon (shoelace). Lanza si el poligono no es simple. */
export function areaPoligono(vertices) {
    const problema = problemaDelPoligono(vertices);
    if (problema)
        throw new GeometriaInvalida(problema);
    return areaSinValidar(vertices);
}
/**
 * Hora del reloj (1..12) de un punto respecto del centro, con 12 h arriba de
 * la imagen. `null` en el centro mismo.
 */
export function posicionHoraria(punto, centro) {
    const dx = punto.x - centro.x;
    const dy = punto.y - centro.y;
    if (dx === 0 && dy === 0)
        return null;
    const grados = ((((Math.atan2(dx, -dy) * 180) / Math.PI) % 360) + 360) % 360;
    return Math.floor((grados + 15) / 30) % 12 || 12;
}
/**
 * Cuadrante de la imagen: Q1 superior derecho, Q2 superior izquierdo, Q3
 * inferior izquierdo, Q4 inferior derecho. Misma regla que el Python: la
 * horizontal del centro cuenta como inferior y la vertical como derecha.
 */
export function cuadrante(punto, centro) {
    const derecha = punto.x - centro.x >= 0;
    const arriba = punto.y - centro.y < 0;
    if (arriba)
        return derecha ? 'Q1' : 'Q2';
    return derecha ? 'Q4' : 'Q3';
}
/** Escala a partir de una referencia de longitud conocida. */
export function pxPorMmDeReferencia(referenciaPx, referenciaMm) {
    for (const [valor, nombre] of [
        [referenciaPx, 'la distancia de referencia en píxeles'],
        [referenciaMm, 'la distancia de referencia en milímetros'],
    ]) {
        if (!Number.isFinite(valor) || valor <= 0) {
            throw new GeometriaInvalida(`El valor de ${nombre} debe ser un número positivo.`);
        }
    }
    return referenciaPx / referenciaMm;
}
const redondear = (valor, decimales) => Math.round(valor * 10 ** decimales) / 10 ** decimales;
/**
 * Calcula una medicion sobre una imagen de `ancho` x `alto` pixeles.
 *
 * - Distancia: en pixeles y en mm; la ubicacion es la del punto medio.
 * - Area: en px² y en mm² (la escala va al cuadrado); la ubicacion es la del
 *   promedio de los vertices.
 *
 * Sin calibracion se usa la escala nominal y el valor sale marcado `nominal`:
 * no hay forma de obtener un milimetro sin saber de donde salio.
 */
export function medir(medicion, imagen, escala, cervix) {
    const enPx = medicion.puntos.map((p) => ({ x: p.x * imagen.ancho, y: p.y * imagen.alto }));
    let valorPx;
    let referencia;
    if (medicion.tipo === 'distancia') {
        if (enPx.length !== 2)
            throw new GeometriaInvalida('Una distancia lleva exactamente dos puntos.');
        valorPx = distancia(enPx[0], enPx[1]);
        referencia = { x: (enPx[0].x + enPx[1].x) / 2, y: (enPx[0].y + enPx[1].y) / 2 };
    }
    else {
        valorPx = areaPoligono(enPx);
        referencia = {
            x: enPx.reduce((s, p) => s + p.x, 0) / enPx.length,
            y: enPx.reduce((s, p) => s + p.y, 0) / enPx.length,
        };
    }
    const pxPorMm = escala.pxPorMm ?? PX_POR_MM_NOMINAL;
    const valorMm = pxPorMm > 0 ? valorPx / (medicion.tipo === 'area' ? pxPorMm * pxPorMm : pxPorMm) : null;
    const centro = cervix ? { x: cervix.cx, y: cervix.cy } : null;
    return {
        valorPx: redondear(valorPx, 1),
        valorMm: valorMm === null ? null : redondear(valorMm, 2),
        modo: escala.pxPorMm === null ? 'nominal' : escala.modo,
        posicionHoraria: centro ? posicionHoraria(referencia, centro) : null,
        cuadrante: centro ? cuadrante(referencia, centro) : null,
    };
}
/** El cervix normalizado (0..1) llevado a pixeles de una imagen. */
export function cervixEnPx(cervix, imagen) {
    return {
        cx: cervix.cx * imagen.ancho,
        cy: cervix.cy * imagen.alto,
        r: cervix.r * Math.min(imagen.ancho, imagen.alto),
    };
}
//# sourceMappingURL=geometria.js.map